<script setup>
import { computed, onBeforeMount, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useStoreGetters, useStore } from 'dashboard/composables/store';

import NextButton from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  // Ausente ao criar; presente ao editar.
  customRole: {
    type: Object,
    default: null,
  },
});
const emit = defineEmits(['close']);

const { t, te } = useI18n();
const store = useStore();
const getters = useStoreGetters();

const isEditing = computed(() => Boolean(props.customRole?.id));

const name = ref(props.customRole?.name ?? '');
const description = ref(props.customRole?.description ?? '');
const permissions = ref([...(props.customRole?.permissions ?? [])]);

const uiFlags = computed(() => getters['customRoles/getUIFlags'].value);
const availablePermissions = computed(
  () => getters['customRoles/getAvailablePermissions'].value
);
const isSaving = computed(
  () => uiFlags.value.isCreating || uiFlags.value.isUpdating
);
// Papel sem permissão não concede nada e fica indistinguível, na tela de Agentes, de um papel que
// concede — o backend recusa, e o botão não deixa chegar lá.
const isInvalid = computed(
  () => !name.value.trim() || permissions.value.length === 0
);

// Permissão nova no catálogo aparece mesmo sem tradução, com a própria chave: feia é melhor que
// invisível, porque invisível ninguém consegue conceder.
const permissionLabel = chave => {
  const i18nKey = `CUSTOM_ROLE_MGMT.PERMISSIONS.${chave.toUpperCase()}`;
  return te(i18nKey) ? t(i18nKey) : chave;
};

const onClose = () => emit('close');

const onSubmit = async () => {
  const payload = {
    name: name.value.trim(),
    description: description.value.trim(),
    permissions: permissions.value,
  };

  try {
    if (isEditing.value) {
      await store.dispatch('customRoles/update', {
        id: props.customRole.id,
        ...payload,
      });
      useAlert(t('CUSTOM_ROLE_MGMT.EDIT.API.SUCCESS_MESSAGE'));
    } else {
      await store.dispatch('customRoles/create', payload);
      useAlert(t('CUSTOM_ROLE_MGMT.ADD.API.SUCCESS_MESSAGE'));
    }
    onClose();
  } catch (error) {
    // A mensagem do servidor é a útil: ela nomeia a permissão desconhecida ou o nome repetido.
    useAlert(
      error?.response?.data?.message ||
        t('CUSTOM_ROLE_MGMT.FORM.API.ERROR_MESSAGE')
    );
  }
};

onBeforeMount(() => {
  if (!availablePermissions.value.length) {
    store.dispatch('customRoles/getAvailablePermissions');
  }
});
</script>

<template>
  <div class="flex flex-col h-auto overflow-auto">
    <woot-modal-header
      :header-title="
        isEditing
          ? $t('CUSTOM_ROLE_MGMT.EDIT.TITLE')
          : $t('CUSTOM_ROLE_MGMT.ADD.TITLE')
      "
      :header-content="$t('CUSTOM_ROLE_MGMT.FORM.DESC')"
    />
    <form class="flex flex-wrap mx-0" @submit.prevent="onSubmit">
      <woot-input
        v-model="name"
        class="w-full"
        :label="$t('CUSTOM_ROLE_MGMT.FORM.NAME.LABEL')"
        :placeholder="$t('CUSTOM_ROLE_MGMT.FORM.NAME.PLACEHOLDER')"
      />

      <woot-input
        v-model="description"
        class="w-full"
        :label="$t('CUSTOM_ROLE_MGMT.FORM.DESCRIPTION.LABEL')"
        :placeholder="$t('CUSTOM_ROLE_MGMT.FORM.DESCRIPTION.PLACEHOLDER')"
      />

      <div class="w-full mt-2">
        <label class="text-body-main text-n-slate-12">
          {{ $t('CUSTOM_ROLE_MGMT.FORM.PERMISSIONS.LABEL') }}
        </label>
        <p class="text-body-main text-n-slate-11">
          {{ $t('CUSTOM_ROLE_MGMT.FORM.PERMISSIONS.HELP') }}
        </p>
        <div class="grid grid-cols-1 gap-2 mt-2 sm:grid-cols-2">
          <label
            v-for="permission in availablePermissions"
            :key="permission"
            class="flex items-center gap-2 text-body-main text-n-slate-12"
          >
            <input v-model="permissions" type="checkbox" :value="permission" />
            {{ permissionLabel(permission) }}
          </label>
        </div>
      </div>

      <div class="flex items-center justify-end w-full gap-2 px-0 py-2">
        <NextButton
          faded
          slate
          type="reset"
          :label="$t('CUSTOM_ROLE_MGMT.FORM.CANCEL')"
          @click.prevent="onClose"
        />
        <NextButton
          type="submit"
          :label="
            isEditing
              ? $t('CUSTOM_ROLE_MGMT.FORM.SAVE')
              : $t('CUSTOM_ROLE_MGMT.FORM.CREATE')
          "
          :disabled="isInvalid || isSaving"
          :is-loading="isSaving"
        />
      </div>
    </form>
  </div>
</template>
